import { GiftCardDetails } from '../models/serializable/gift-card/gift-card-details.js';
import { GiftCardDiscountProvider } from '../discounts/gift-card-discount-provider.js';
import { Price } from '../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../utility/null-safety.js';
import { Discount, DiscountType } from '../models/serializable/discounts/discount.js';
import { EventTicket } from '../models/serializable/event-ticket.js';
import { CartDiscountProvider, DiscountProvider, NoDiscountProvider } from '../discounts/discount-provider.js';
import { EventPromoCodeDiscountProvider } from '../discounts/event-promo-code-discount-provider.js';
import { TicketGiftCardDiscountProvider } from '../discounts/ticket-gift-card-discount-provider.js';
import { OpenTicketPassDiscountProvider } from '../discounts/open-ticket-pass-discount-provider.js';
import { TrackingPromoCodeDiscountProvider } from '../discounts/tracking-promo-code-discount-provider.js';
import { DiscountCodeBase } from '../contracts/discount-code.js';
import { EventPromoCode } from '../models/serializable/discounts/event-promo-code.js';
import { TicketGiftCard } from '../models/serializable/discounts/ticket-gift-card.js';
import { OpenTicketPass } from '../models/serializable/discounts/open-ticket-pass.js';
import { TrackingPromoCode } from '../models/serializable/discounts/tracking-promo-code.js';
import { BusinessPromoCode } from '../models/serializable/discounts/business-promo-code.js';
import { BusinessPromoCodeDiscountProvider } from '../discounts/business-promo-code-discount-provider.js';
import { OpenAccessPassDetails } from '../models/serializable/open-access-pass/open-access-pass-details.js';
import { FlexiblePassProtocol } from '../models/serializable/open-access-pass/flexible-pass-protocol.js';
import { FlexiblePassDiscountProvider } from '../discounts/flexible-pass-discount-provider.js';

export abstract class DiscountHelper {
  static hasProvider(discountCode: DiscountCodeBase): boolean {
    return EventPromoCode.verifyType(discountCode) ||
      BusinessPromoCode.verifyType(discountCode) ||
      TrackingPromoCode.verifyType(discountCode) ||
      TicketGiftCard.verifyType(discountCode) ||
      GiftCardDetails.verifyType(discountCode) ||
      OpenTicketPass.verifyType(discountCode) ||
      (OpenAccessPassDetails.assertType(discountCode) && FlexiblePassProtocol.verifyType(discountCode.protocol));
  }

  static getProvider(discountCode: DiscountCodeBase | null): DiscountProvider {
    if(isNullOrUndefined(discountCode)) {
      return new NoDiscountProvider();
    }

    if(EventPromoCode.assertType(discountCode)) {
      return new EventPromoCodeDiscountProvider(discountCode);
    }
    if(BusinessPromoCode.assertType(discountCode)) {
      return new BusinessPromoCodeDiscountProvider(discountCode);
    }
    if(TrackingPromoCode.assertType(discountCode)) {
      return new TrackingPromoCodeDiscountProvider(discountCode);
    }
    if(GiftCardDetails.assertType(discountCode)) {
      return new GiftCardDiscountProvider(discountCode);
    }
    if(TicketGiftCard.assertType(discountCode)) {
      return new TicketGiftCardDiscountProvider(discountCode);
    }
    if(OpenTicketPass.assertType(discountCode)) {
      return new OpenTicketPassDiscountProvider(discountCode);
    }
    if(OpenAccessPassDetails.assertType(discountCode) && FlexiblePassProtocol.verifyType(discountCode.protocol)) {
      return new FlexiblePassDiscountProvider(discountCode);
    }

    return new NoDiscountProvider();
  }

  static getProviderForCart(discount: Discount | null): DiscountProvider {
    return discount != null ?
      CartDiscountProvider.create(discount):
      new NoDiscountProvider();
  }

  static hasDiscountValue(discount: Discount | null): boolean {
    return discount != null && (
      (discount.type === DiscountType.Percentage && discount.percentage != null) ||
      ((discount.type === DiscountType.Fixed || discount.type === DiscountType.DownToPrice) && discount.fixedPrice != null)
    );
  }

  static calculateDiscountValueForSingleItem(originalPrice: Price, discount: Discount): Price | null {
    return this.hasDiscountValue(discount) ?
      originalPrice.difference(this.calculateDiscountedPriceForSingleItem(originalPrice, discount)) : null;
  }

  static calculateDiscountedPriceForSingleItem(originalPrice: Price, discount: Discount | null): Price {
    let discountedPrice = originalPrice;

    if(isNotNullOrUndefined(discount)) {
      if(discount.type === DiscountType.Fixed && isNotNullOrUndefined(discount.fixedPrice)) {
        discountedPrice = this.applyFixedDiscountToPrice(originalPrice, discount.fixedPrice);
      }
      if(discount.type === DiscountType.Percentage && isNotNullOrUndefined(discount.percentage)) {
        discountedPrice = this.applyPercentageDiscountToPrice(originalPrice, discount.percentage);
      }
      if(discount.type === DiscountType.DownToPrice && isNotNullOrUndefined(discount.fixedPrice)) {
        discountedPrice = this.applyDownToPriceDiscountToPrice(originalPrice, discount.fixedPrice);
      }
    }

    return discountedPrice;
  }

  static calculateDiscountedPriceForMultipleItems(unitPrice: Price, quantity: number, discount: Discount | null): Price {
    const pricePerUnit = this.calculateDiscountedPriceForSingleItem(unitPrice, discount);
    return pricePerUnit.scale(quantity);
  }

  static getFormattedPriceWithDiscountForTicket(ticket: EventTicket): string {
    const ticketPrice = ticket.template.price;
    const discount = ticket.discount;
    let formattedPrice = this.calculateDiscountedPriceForSingleItem(ticketPrice, discount).format();

    if(isNotNullOrUndefined(discount)) {
      let formattedDiscount: string | null = null;

      if(discount.type === DiscountType.Fixed && isNotNullOrUndefined(discount.fixedPrice)) {
        formattedDiscount = `-${discount.fixedPrice.format()}`;
      }
      if(discount.type === DiscountType.Percentage && isNotNullOrUndefined(discount.percentage)) {
        formattedDiscount = `-${discount.percentage.toFixed(0)}%`;
      }
      if(discount.type === DiscountType.DownToPrice && isNotNullOrUndefined(discount.fixedPrice)) {
        if(ticketPrice.compareTo(discount.fixedPrice) >= 0) {
          formattedDiscount = `-${(ticketPrice.difference(discount.fixedPrice)).format()}`;
        }
      }
      if(discount.type === DiscountType.Legacy && isNotNullOrUndefined(discount.percentage)) {
        formattedDiscount = `-${discount.percentage.toFixed(0)}%`;
      }

      if(formattedDiscount != null && discount.code != null) {
        formattedDiscount = `${formattedDiscount} - (${discount.code.toUpperCase()})`;
      }

      if(isNotNullOrUndefined(formattedDiscount)) {
        formattedPrice = `${formattedPrice} (${formattedDiscount})`;
      }
    }

    return formattedPrice;
  }

  static applyPercentageDiscountToPrice(price: Price, discount: number): Price {
    const sanitizedDiscount = Math.max(0, Math.min(100, discount));
    const discountPercents = sanitizedDiscount / 100.0;

    return price.scale(1.0 - discountPercents);
  }

  static applyFixedDiscountToPrice(price: Price, discount: Price): Price {
    const discountedPrice = price.difference(discount);
    return discountedPrice.isNegative() ? Price.zero(price.currency) : discountedPrice;
  }

  static applyDownToPriceDiscountToPrice(price: Price, desiredPrice: Price): Price {
    if(price.compareTo(desiredPrice) <= 0) {
      return price.clone();
    }

    return desiredPrice.clone();
  }
}
